import { Box, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { HomeWorkOutlined } from '@material-ui/icons';
import { insertIf, PURCHASE, REFINANCE } from '@roc/feature-utils';
import { observer } from 'mobx-react';
import { EditControls } from '../../../components/editControls';
import { RoundedBackgroundIcon } from '../../../components/roundedBackgroundIcon';
import { TitleDivider } from '../../../components/titleDivider';
import { WholesalerAlertMessage } from '../../../components/wholesalerAlertMessage';
import { PropertyFormStore } from '../../../stores/properties/propertyFormStore';
import { QuoteTypeStore } from '../../../stores/quoteTypeStore';
import { FORM_MAX_WIDTH, propertySourceValues, propertyTypeOptions } from '../../../utils/constants';
import {
  AnnualHOAProvidedAtOriginationValue,
  AnnualInsurance,
  AnnualTaxes,
  ArmsLengthComment,
  BorrowerKnowOwnerOfRecord,
  ConstructionRenoSpendToDate,
  County,
  CurrentlyOccupied,
  CurrentMarketValue,
  HasBeenRecentlyRenovated,
  HoldingCosts,
  MonthlyMarketRent,
  OutstandingLoanOnTheProperty,
  PayoffAmount,
  PropertyAddress,
  PropertyLeased,
  PropertyOwnership,
  PropertySourcing,
  PropertySourcingExplanation,
  PropertyType,
  ProposedConstructionBudget,
  PurchaseDate,
  PurchasePrice,
  Section8,
  SeekingCashOut,
  SellerCredit,
  SellerCreditAmount,
  SoonToBeListedForSale,
  WhatWasThePurchasePrice,
  WholesalerPropertyContractAmount,
} from '../components/propertyInformationFields';
import {
  PropertyInformationTimeline,
  TimelineIconConstructionBudget,
  TimelineIconMarketValue,
  TimelineIconPurchasePrice,
  TimelineIconRenoSpent,
} from '../components/propertyInformationTimeline';
import { NetOperatingIncome } from 'libs/feature-loans/src/rentalPricer/pricerSteps/components/netOperatingIncome';
import { ItemDisplay } from '../../../components/itemDisplay';
import { formatCurrency } from '@roc/ui/utils';
import { useEffect } from 'react';
import { RuralPropertyWarning } from 'libs/feature-loans/src/loanSubmission/components/ruralPropertyWarning';
import { buildAddress } from '../../../utils/utils';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    position: 'relative',
    maxWidth: 1300,
    margin: 'auto',
  },
  activeConnector: {
    height: '50px',
    backgroundColor: theme.palette.primary.main,
    width: '4px',
  },
  inactiveConnector: {
    height: '50px',
    width: '4px',
  },
  substractionContainer: {
    display: 'flex',
    width: '90%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: '20px'
  },
  symbols: {
    color: '#BABEC5',
    fontSize: '60px',
    fontWeight: 300
  },
  timelineDot: {},
}));
interface PropertyInformationFormProps {
  store: PropertyFormStore;
  onDeleteClick: (propertyKey: string) => void;
  removeButtonDisabled?: boolean;
  showActions: boolean;
  quoteTypeStore: QuoteTypeStore;
}

export const SinglePropertyRentalPropertyInformationForm = observer(
  ({
    store,
    onDeleteClick,
    showActions = true,
    removeButtonDisabled = false,
    quoteTypeStore
  }: PropertyInformationFormProps) => {

    useEffect(() => {
      quoteTypeStore.fetchStateCounties();
    }, [store.form.fields.state.value])

    useEffect(() => {
      store.checkIfPropertyIsRural(buildAddress(store.getProperty()));
    }, [store.form.fields.zipCode.value]);

    const classes = useStyles();
    const propertyKey = store.form.meta.key;

    function saveHandler() {
      const isValid = store.validateAndCleanupProperty(
        'Please complete all the required fields'
      );
      if (isValid) {
        store.setEditMode(false);
      }
    }

    const loanPurpose = store.form.fields.loanPurpose.value;

    const isPurchase = loanPurpose !== REFINANCE;
    const isRefinance = loanPurpose === REFINANCE;

    const propertySourcing = store.form.fields.propertySourcing.value;

    const displayWholesalerAlertMessage =
      isPurchase && propertySourcing === propertySourceValues.wholesaler;

    const showPropertySourcingExplanation =
      isPurchase && propertySourcing === propertySourceValues.other;

    const calculatedIncome = (Number(store?.form?.fields?.monthlyMarketRent?.value) ?? 0) * 12;
    const calculatedExpenses = (Number(store?.form?.fields?.annualHOAProvidedAtOrigination?.value) ?? 0) + (Number(store?.form?.fields?.annualTaxes?.value) ?? 0) + (Number(store?.form?.fields?.annualInsurance?.value) ?? 0);
    const calculatedOperatingIncome = calculatedIncome - calculatedExpenses;
    store.getTotalExpenses()

    return (
      <>
        <Grid
          container
          spacing={2}
          className={classes.container}
          style={{ maxWidth: FORM_MAX_WIDTH, marginBottom: '0.5rem' }}
        >
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              justifyContent="flex-start"
              alignItems="center"
            >
              <Grid item>
                <RoundedBackgroundIcon
                  Icon={HomeWorkOutlined}
                  iconColor="#6D6D6D"
                />
              </Grid>
              <Grid item>
                <Typography variant="h4" style={{ color: '#6D6D6D' }}>
                  Add Property
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            style={{ marginTop: '1rem', marginBottom: '1rem' }}
          >
            <TitleDivider title="Property Details" />
          </Grid>
          <Grid item xs={12}>
            <PropertyAddress store={store} quoteTypeStore={quoteTypeStore} />
          </Grid>
          {store.isPropertyMSARural &&
            <Grid item xs={12}>
              <RuralPropertyWarning store={quoteTypeStore} />
            </Grid>
          }
          {store.form.fields.address.value &&
            <Grid item xs={12} sm={6}>
              <County store={store} counties={quoteTypeStore?.counties ?? []} />
            </Grid>
          }
          <Grid item xs={12} sm={store.form.fields.address.value ? 6 : 12}>
            <PropertyType store={store} options={propertyTypeOptions} />
          </Grid>
          <Grid item xs={12} />
          <Grid item xs={12} sm={6}>
            <PropertyOwnership store={store} />
          </Grid>
          {loanPurpose === PURCHASE &&
            <Grid item xs={12} sm={6}>
              <PropertyLeased store={store}></PropertyLeased>
            </Grid>
          }
          <Grid item xs={12} sm={6}>
            <Section8 store={store}></Section8>
          </Grid>

          {isRefinance && <RefinanceFields store={store} />}
        </Grid>
        <Grid
          container
          spacing={2}
          className={classes.container}
          justifyContent="center"
          style={{ maxWidth: FORM_MAX_WIDTH, paddingInline: 0 }}
        >
          <Grid
            item
            xs={12}
            style={{ marginTop: '1rem', marginBottom: '1rem' }}
          >
            <TitleDivider title="Project Details" />
          </Grid>
          {displayWholesalerAlertMessage && (
            <Grid item xs={12} style={{ paddingInline: '1rem' }}>
              <WholesalerPropertyContractAmount store={store} />
            </Grid>
          )}
          {showPropertySourcingExplanation && (
            <Grid item xs={12} style={{ paddingInline: '1rem' }}>
              <PropertySourcingExplanation store={store} />
            </Grid>
          )}
          <Grid item xs={12}>
            <PropertyInformationTimeline
              items={[
                {
                  icon: <TimelineIconPurchasePrice />,
                  ...(isPurchase
                    ? { right: <PurchasePrice store={store} /> }
                    : { left: <WhatWasThePurchasePrice store={store} /> }),
                },
                ...insertIf(!isPurchase, [
                  {
                    icon: <TimelineIconMarketValue />,
                    left: <ConstructionRenoSpendToDate store={store} />,
                  },
                ]),

                {
                  icon: <TimelineIconRenoSpent />,
                  right: <CurrentMarketValue store={store} />,
                },
                {
                  icon: <TimelineIconMarketValue />,
                  right: <MonthlyMarketRent store={store} />,
                  gray: true
                },
                {
                  icon: <TimelineIconRenoSpent />,
                  right: <AnnualTaxes store={store} />,
                  gray: true
                },
                {
                  icon: <TimelineIconMarketValue />,
                  right: <AnnualInsurance store={store} />,
                  gray: true
                },
                {
                  icon: <TimelineIconMarketValue />,
                  right: <AnnualHOAProvidedAtOriginationValue addMarkForRequiredFields={true}
                    isRequired={true} standaloneLabel={false} store={store} />,
                  gray: true
                },
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <Box className={classes.substractionContainer}>
              <ItemDisplay bottomLabel label="Total Annual Income" value={`${formatCurrency(calculatedIncome ?? 0)}`}></ItemDisplay>
              <Typography className={classes.symbols} align="center" variant="h4">
                -
              </Typography>
              <ItemDisplay bottomLabel label="Annual Expenses" value={`${formatCurrency(calculatedExpenses ?? 0)}`}></ItemDisplay>
              <Typography className={classes.symbols} align="center" variant="h4">
                =
              </Typography>
              <ItemDisplay bottomLabel label="Annual NOI" value={`${formatCurrency(calculatedOperatingIncome ?? 0)}`}></ItemDisplay>
            </Box>
          </Grid>

        </Grid>
        {showActions && (
          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
            style={{ padding: '1rem' }}
          >
            <EditControls
              onDeleteClick={() => onDeleteClick(propertyKey)}
              onSaveClick={saveHandler}
              removeButtonDisabled={removeButtonDisabled}
            />
          </Grid>
        )}
      </>
    );
  }
);

const RefinanceFields = observer(({ store }) => {
  const outstandingLoanOnTheProperty =
    store.form.fields.outstandingLoanOnTheProperty.value;

  return (
    <>
      <Grid item xs={12} sm={6}>
        <PurchaseDate store={store} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <OutstandingLoanOnTheProperty store={store} />
      </Grid>
      {outstandingLoanOnTheProperty && (
        <Grid item xs={12} sm={6}>
          <PayoffAmount store={store} />
        </Grid>
      )}
      <Grid xs={12} />
      {outstandingLoanOnTheProperty && (
        <Grid item xs={12} sm={6}>
          <SeekingCashOut store={store} />
        </Grid>)}
    </>
  );
});
